import { useState, useEffect } from "react";

// ─── DATA ────────────────────────────────────────────────────────────────────

const MODULES = [
  {
    id: 1, title: "What is AI?", emoji: "🤖", color: "#FF6B6B", bg: "#FFF0F0", dark: "#C0392B",
    xp: 100, age: "8-12", duration: "15 min", world: "Daily Life", mindfulness: "Breathing",
    desc: "Discover how AI thinks and works around you every day",
    lessons: [
      {
        type: "mindful", title: "Let's Breathe First 🌬️",
        content: "Before we dive in, let's calm our minds. Take 3 slow deep breaths with me. In for 4 counts... hold for 4... out for 4. A calm brain learns twice as fast!",
        fact: "Science shows that deep breathing increases oxygen to your brain — making you smarter instantly!",
        world: "Just like stretching before sport, mindful breathing warms up your brain for learning!",
        activity: "breathing"
      },
      {
        type: "learn", title: "AI is Everywhere! 🌍",
        content: "Artificial Intelligence (AI) is when computers learn to do tasks that usually need human thinking — like recognising your face, understanding your voice, or recommending your next favourite song!",
        fact: "Your phone's face unlock uses AI to recognise YOU out of 8 billion people on Earth!",
        world: "When Netflix suggests a show you end up loving — that's AI studying your tastes!"
      },
      {
        type: "quiz", question: "Which of these uses AI in everyday life?",
        options: ["A light switch", "Netflix recommending shows", "A paper book", "A pencil"],
        correct: 1,
        explain: "Netflix uses AI to study your watch history and suggest shows you will love!"
      },
      {
        type: "scenario", title: "Spot the Spam! 🕵️",
        desc: "You're an AI email filter. Tap which emails are SPAM:",
        items: ["You won $1,000,000!!!", "Meeting tomorrow at 3pm", "FREE iPHONE CLICK NOW!", "Project update from Sarah"],
        spam: [0, 2],
        hint: "AI spots suspicious patterns: ALL CAPS, exclamation marks, too-good-to-be-true offers!",
        world: "Real spam filters use exactly this kind of pattern-matching AI to protect your inbox!"
      },
      {
        type: "activity", title: "🎨 Draw Your AI Brain!",
        instructions: "On paper, draw a robot brain. On one side write 5 things AI knows (faces, songs, words). On the other side write 5 things only YOU know (how a hug feels, the smell of rain, your best memory). Compare them!",
        prompts: ["What makes human intelligence special?", "What can AI do that humans find hard?", "What can you do that AI never can?"],
        world: "AI can recognise your face in a photo — but it has no idea how happy you feel when you see your best friend!",
        parentTip: "Ask your child to explain their drawing. This builds 'metacognition' — thinking about thinking — a top skill for future AI designers!",
        teacherTip: "Host a class gallery walk where students share their 'Human vs AI Brain' drawings and vote on the most creative!"
      }
    ]
  },
  {
    id: 2, title: "Machine Learning", emoji: "🧠", color: "#4ECDC4", bg: "#F0FFFE", dark: "#00695C",
    xp: 150, age: "9-13", duration: "20 min", world: "Cooking & Recipes", mindfulness: "Observation",
    desc: "See how computers learn from examples — just like you do!",
    lessons: [
      {
        type: "mindful", title: "Mindful Observation 👁️",
        content: "Look around you right now. Name 5 things you can SEE, 4 you can TOUCH, 3 you can HEAR. This present-moment awareness is exactly how AI 'observes' the world — by paying attention to details!",
        fact: "Being mindfully observant makes you a better learner AND a better AI designer — both need to notice patterns others miss!",
        world: "AI observes thousands of examples the same way you're observing your room right now — noticing every detail!",
        activity: "observation"
      },
      {
        type: "learn", title: "Learning by Example 📚",
        content: "Machine Learning is how AI gets smarter! Instead of being programmed with every single rule, AI learns by studying thousands of examples — just like how you learned to recognise cats by seeing lots of cats growing up!",
        fact: "To learn to recognise a cat, AI might study over 1 MILLION cat photos — that's more cats than you'd see in 1,000 lifetimes!",
        world: "When your parent teaches you a recipe by showing you each step, that's the same as training an AI with labelled examples!"
      },
      {
        type: "quiz", question: "How does Machine Learning actually work?",
        options: ["A human types every single rule", "AI finds patterns by studying lots of examples", "AI reads textbooks overnight", "Pure magic inside the computer"],
        correct: 1,
        explain: "Machine Learning finds patterns in data automatically — no manual rule-writing needed!"
      },
      {
        type: "label", title: "Train the AI! 🏷️",
        desc: "Help teach an AI to recognise fruit! Match each description to its fruit:",
        items: ["Red, round, sweet & crunchy", "Yellow, long & curved", "Purple, tiny & grows in bunches", "Orange, round & citrusy"],
        labels: ["Apple", "Banana", "Grapes", "Orange"],
        emojis: ["🍎", "🍌", "🍇", "🍊"],
        hint: "AI learns by connecting features (colour, shape, size) to labels — exactly what you're doing!",
        world: "Every time you tag a photo on social media, you're teaching AI what's in images!"
      },
      {
        type: "activity", title: "✍️ Write an AI Recipe!",
        instructions: "Create a 'recipe' to teach AI to recognise YOUR bedroom! List: 5 things always there, 3 colours it would see, 2 sounds it might hear. This is called 'feature extraction' — a real AI engineering technique!",
        prompts: ["What makes your room unique from any other room?", "Could two rooms have all the same features?", "How would AI know it's YOUR room and not a stranger's?"],
        world: "Real AI engineers write exactly these kinds of feature descriptions when teaching computers to understand environments!",
        parentTip: "Walk through your home together doing 'feature extraction' in different rooms — make it an I-Spy game!",
        teacherTip: "Have students swap their 'room recipes' and try to guess whose room is described. Discuss why some are easier to identify!"
      }
    ]
  },
  {
    id: 3, title: "Neural Networks", emoji: "⚡", color: "#A78BFA", bg: "#F5F0FF", dark: "#5B21B6",
    xp: 200, age: "10-14", duration: "20 min", world: "Your Brain", mindfulness: "Body Scan",
    desc: "Explore the brain-inspired technology powering modern AI",
    lessons: [
      {
        type: "mindful", title: "Body Scan Moment 🌿",
        content: "Close your eyes for 20 seconds. Notice your feet on the floor... your hands in your lap... your breath moving in and out. Your body is an incredible network of signals — and neural networks work the same way!",
        fact: "Your brain has 86 billion neurons all talking to each other — the inspiration for AI's neural networks!",
        world: "Every thought you have is neurons firing in sequence — exactly like data flowing through an AI neural network!",
        activity: "bodyscan"
      },
      {
        type: "learn", title: "Inspired by Your Brain 🧬",
        content: "Neural networks are AI systems modelled after the human brain! They have layers of connected 'neurons' that pass information and learn patterns. Deep Learning uses MANY layers — that's why it's called DEEP! Each layer learns something more complex than the last.",
        fact: "GPT-4 has about 1.8 TRILLION parameters — like having 1.8 trillion brain connections all working together!",
        world: "When you look at a photo and instantly see a dog, millions of your neurons fire in layers — first edges, then shapes, then 'dog'. Neural networks do the exact same thing!"
      },
      {
        type: "quiz", question: "What inspired the design of Neural Networks?",
        options: ["Computer circuit boards", "The human brain's neurons", "The internet's cable network", "DNA's double helix"],
        correct: 1,
        explain: "Neural networks mimic how brain neurons connect, communicate and strengthen connections through experience!"
      },
      {
        type: "quiz", question: "What makes 'Deep' Learning deep?",
        options: ["It digs underground for data", "It has many layers of neurons processing information", "It takes a really long time", "It uses very large computers"],
        correct: 1,
        explain: "Each 'deep' layer learns increasingly complex features — from simple edges to complex concepts!"
      },
      {
        type: "activity", title: "🕸️ Draw a Neural Network!",
        instructions: "Draw 3 columns of circles connected by lines: Column 1 (Input): draw 3 circles labelled 'Fur', 'Tail', 'Ears'. Column 2 (Hidden): draw 3 circles labelled 'Feature 1', 'Feature 2', 'Feature 3'. Column 3 (Output): draw 2 circles labelled 'Dog' and 'Cat'. Draw lines connecting every circle to every circle in the next column. You just drew a real neural network!",
        prompts: ["Why do all circles connect to all circles?", "What happens if you add more circles in the middle?", "Can you add a third output for 'Bird'?"],
        world: "The neural network you just drew is structurally identical to the ones recognising faces in your phone right now!",
        parentTip: "Look up 'neural network playground' by Google together — it lets you train a real neural network in your browser for free!",
        teacherTip: "Act it out! Assign students as neurons. Pass paper 'signals' between them to simulate how data flows through layers."
      }
    ]
  },
  {
    id: 4, title: "AI Helps the World", emoji: "🌍", color: "#F7B731", bg: "#FFFBF0", dark: "#92400E",
    xp: 175, age: "10-15", duration: "25 min", world: "Doctors & Hospitals", mindfulness: "Gratitude",
    desc: "Discover how AI is solving real problems and saving real lives",
    lessons: [
      {
        type: "mindful", title: "Gratitude Moment 💛",
        content: "Think of 3 people who help keep you healthy and safe — a doctor, a teacher, a parent, a friend. Feel grateful for them. AI is being used RIGHT NOW to help all of these people do their jobs better and faster. Let's explore how!",
        fact: "Gratitude activates positive emotion areas in your brain — making you more creative, open, and ready to learn new things!",
        world: "The same AI helping doctors detect cancer early is being developed by people who felt grateful for their own doctors!",
        activity: "gratitude"
      },
      {
        type: "learn", title: "AI Saves Lives Every Day 🏥",
        content: "AI is already helping doctors spot cancer in scans before symptoms appear, helping farmers detect crop diseases from drone photos, predicting floods 48 hours early to evacuate families safely, and instantly translating between 100+ languages so everyone can communicate!",
        fact: "An AI system detected breast cancer with 11.5% fewer false positives than human doctors — meaning fewer unnecessary procedures and more peace of mind!",
        world: "Just like a smoke detector silently protects your home, AI systems silently protect people's health — alerting doctors to problems before patients even feel sick!"
      },
      {
        type: "quiz", question: "A weather app predicts rain tomorrow using 10 years of weather data. Which AI concept is this?",
        options: ["A robot doing exercises", "Machine Learning finding patterns to predict the future", "A phone playing music louder", "A calculator adding rainfall numbers"],
        correct: 1,
        explain: "Weather prediction IS machine learning — finding patterns in years of climate data to predict tomorrow!"
      },
      {
        type: "scenario", title: "Design Your AI Helper! 🌱",
        desc: "Match each real-world problem to the AI solution that best solves it:",
        items: ["Doctors missing early cancer signs", "Farmers losing crops to disease", "Students struggling in class", "Traffic causing pollution"],
        spam: [],
        isDesign: true,
        solutions: ["AI that reads medical scans 24/7", "AI that analyses drone crop photos", "AI tutor that adapts to each learner", "AI that optimises traffic light timing"],
        hint: "Think about what kind of data each AI would need to learn from!",
        world: "All four of these AI systems exist and are being used in the real world today!"
      },
      {
        type: "activity", title: "🚀 Invent an AI for YOUR Community!",
        instructions: "Think of ONE problem in your neighbourhood, school or city. Design an AI to solve it! Give it a name, describe: what data it needs to learn from, what problem it solves, who it helps most, and one way it could accidentally cause harm (and how to prevent it).",
        prompts: ["What problem do you wish someone would just fix?", "What data would your AI need to learn from?", "Who might your AI accidentally leave out or hurt?"],
        world: "The kids in school right now will build the AI that shapes the next 50 years. That might be YOU!",
        parentTip: "Research one AI innovation together (AI in medicine, farming, education). Ask: 'Who benefits? Who might be left behind?'",
        teacherTip: "Host a 'Young AI Inventor' showcase! Students pitch their AI helpers. Class votes on most creative AND most impactful."
      }
    ]
  },
  {
    id: 5, title: "AI Ethics & Fairness", emoji: "⚖️", color: "#34D399", bg: "#F0FDF4", dark: "#065F46",
    xp: 200, age: "11-15", duration: "25 min", world: "School & Fairness", mindfulness: "Compassion",
    desc: "Learn to use AI responsibly, fairly, and for everyone's benefit",
    lessons: [
      {
        type: "mindful", title: "Compassion Practice 💚",
        content: "Imagine a classmate who is new to school and doesn't speak the same language. How would you make sure the school rules feel fair to them? AI faces the exact same challenge — it needs to be designed to be fair to EVERYONE, not just some people.",
        fact: "Practising compassion literally rewires your brain to be better at fairness thinking — the most critical skill for AI designers!",
        world: "Just like school rules should be fair to every student regardless of background, AI needs ethical guidelines to be fair to every person!",
        activity: "compassion"
      },
      {
        type: "learn", title: "When AI Gets It Wrong ⚠️",
        content: "AI can make mistakes — and sometimes unfair ones. If AI learns from biased data, it makes biased decisions. A hiring AI trained mostly on resumes from one type of person may unfairly reject others. A facial recognition system trained on mostly one skin tone works worse for other skin tones. That's why AI Ethics matters so much!",
        fact: "Some facial recognition AIs were 99% accurate for light-skinned men but only 65% accurate for dark-skinned women — a dangerous bias gap!",
        world: "Spell-checkers used to flag names from other cultures as 'wrong'. Companies fixed it when users spoke up — your voice matters!"
      },
      {
        type: "quiz", question: "An AI hiring system automatically rejects applicants from certain neighbourhoods. What should happen?",
        options: ["Nothing — AI is always more objective than humans", "Investigate, fix the bias and audit the system regularly", "Blame the applicants for living there", "Stop using computers for hiring entirely"],
        correct: 1,
        explain: "Spotting and fixing AI bias is one of the most important — and well-paid — jobs in technology today!"
      },
      {
        type: "quiz", question: "What is 'AI Bias'?",
        options: ["When AI prefers certain colours", "When AI makes unfair decisions because it learned from unfair data", "When AI runs too slowly", "When AI uses too much electricity"],
        correct: 1,
        explain: "Biased training data creates biased AI — garbage in, garbage out. Fair data creates fair AI!"
      },
      {
        type: "activity", title: "📜 Write an AI Bill of Rights!",
        instructions: "Write 5 rules that ALL AI systems must follow to be fair, safe and helpful. For each rule, write: the rule itself, who it protects, and what happens if an AI breaks it. Think about: privacy, fairness, transparency, human control, and safety.",
        prompts: ["Should AI always explain WHY it made a decision?", "Who is responsible when AI makes a harmful mistake?", "Should humans always be able to override an AI decision?"],
        world: "The United Nations is RIGHT NOW developing real global AI rights documents — you're thinking exactly like a global policymaker!",
        parentTip: "Read one news story about AI fairness together and discuss: 'Is that fair? Who does it help? Who might it hurt?' This builds real ethical reasoning.",
        teacherTip: "Run a mock 'AI Ethics Court' — students argue cases where AI caused harm. Who is responsible: the AI, the company, or the user?"
      }
    ]
  }
];

const ACHIEVEMENTS = [
  { id: "first", emoji: "🌱", title: "First Step!", desc: "Complete your first lesson", check: s => s.lessons >= 1 },
  { id: "quiz5", emoji: "🎯", title: "Quiz Ace", desc: "Answer 5 questions correctly", check: s => s.correct >= 5 },
  { id: "streak3", emoji: "🔥", title: "On Fire!", desc: "3 correct answers in a row", check: s => s.streak >= 3 },
  { id: "mindful3", emoji: "🧘", title: "Mindful Learner", desc: "Complete 3 mindfulness exercises", check: s => s.mindful >= 3 },
  { id: "module1", emoji: "🏅", title: "Module Master", desc: "Complete your first module", check: s => s.modules >= 1 },
  { id: "xp300", emoji: "⭐", title: "XP Collector", desc: "Earn 300 XP", check: s => s.xp >= 300 },
  { id: "module3", emoji: "🚀", title: "AI Explorer", desc: "Complete 3 modules", check: s => s.modules >= 3 },
  { id: "allmodules", emoji: "👑", title: "AI Champion!", desc: "Complete all 5 modules", check: s => s.modules >= 5 },
];

// ─── BREATHING COMPONENT ─────────────────────────────────────────────────────
function BreathingEx({ color, onDone }) {
  const [phase, setPhase] = useState("inhale");
  const [count, setCount] = useState(4);
  const [cycles, setCycles] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      setCount(c => {
        if (c <= 1) {
          setPhase(p => {
            if (p === "inhale") return "hold";
            if (p === "hold") return "exhale";
            setCycles(prev => {
              if (prev + 1 >= 3) { setDone(true); }
              return prev + 1;
            });
            return "inhale";
          });
          return 4;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [done]);

  const phaseInfo = {
    inhale: { label: "Breathe IN", scale: "1.3" },
    hold: { label: "Hold...", scale: "1.15" },
    exhale: { label: "Breathe OUT", scale: "0.85" }
  };

  if (done) return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🌟</div>
      <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 20, color: "#10B981", marginBottom: 8 }}>Beautiful! Your brain is ready!</div>
      <button onClick={onDone} style={{ padding: "12px 28px", background: color, color: "white", border: "none", borderRadius: 14, fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>Continue →</button>
    </div>
  );

  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ width: 100, height: 100, borderRadius: "50%", background: color, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, transition: "transform 0.8s ease", transform: `scale(${phaseInfo[phase].scale})` }}>
        🌬️
      </div>
      <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 22, color, marginBottom: 6 }}>{phaseInfo[phase].label}</div>
      <div style={{ fontSize: 36, fontWeight: 900, color: "#1F2937", marginBottom: 8 }}>{count}</div>
      <div style={{ fontSize: 13, color: "#9CA3AF", fontWeight: 700 }}>Round {cycles + 1} of 3</div>
    </div>
  );
}

// ─── GRATITUDE COMPONENT ──────────────────────────────────────────────────────
function GratitudeEx({ color, onDone }) {
  const [notes, setNotes] = useState(["", "", ""]);
  const [submitted, setSubmitted] = useState(false);
  if (submitted) return (
    <div style={{ textAlign: "center", padding: "16px 0" }}>
      <div style={{ fontSize: 44, marginBottom: 10 }}>💛</div>
      <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color, marginBottom: 8 }}>You're grateful for: {notes.filter(n => n).join(", ")}!</div>
      <p style={{ fontSize: 13, color: "#6B7280", fontWeight: 700, marginBottom: 16 }}>These real people are who AI is being designed to help and protect. Keep them in mind!</p>
      <button onClick={onDone} style={{ padding: "12px 28px", background: color, color: "white", border: "none", borderRadius: 14, fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>Continue →</button>
    </div>
  );
  return (
    <div>
      <p style={{ fontSize: 14, fontWeight: 700, color: "#6B7280", marginBottom: 14 }}>Name 3 people who keep you safe or happy 💛</p>
      {notes.map((n, i) => (
        <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
          <span style={{ fontSize: 20 }}>{"💛🧡❤️"[i]}</span>
          <input value={n} onChange={e => { const a = [...notes]; a[i] = e.target.value; setNotes(a); }}
            placeholder={`Person ${i + 1}...`}
            style={{ flex: 1, padding: "10px 14px", borderRadius: 12, border: "2px solid #E5E7EB", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 14, outline: "none" }} />
        </div>
      ))}
      <button onClick={() => setSubmitted(true)} disabled={notes.some(n => !n.trim())}
        style={{ width: "100%", marginTop: 8, padding: "12px", background: notes.some(n => !n.trim()) ? "#E5E7EB" : color, color: notes.some(n => !n.trim()) ? "#9CA3AF" : "white", border: "none", borderRadius: 14, fontFamily: "'Nunito',sans-serif", fontWeight: 900, fontSize: 15, cursor: notes.some(n => !n.trim()) ? "default" : "pointer" }}>
        Share Gratitude 💛
      </button>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("home");
  const [activeModule, setActiveModule] = useState(null);
  const [lessonIdx, setLessonIdx] = useState(0);
  const [stats, setStats] = useState({ xp: 0, lessons: 0, correct: 0, streak: 0, mindful: 0, modules: 0, doneModules: [] });
  const [earned, setEarned] = useState([]);
  const [newAch, setNewAch] = useState(null);
  const [xpPop, setXpPop] = useState(null);
  const [quizAns, setQuizAns] = useState(null);
  const [quizDone, setQuizDone] = useState(false);
  const [spamSel, setSpamSel] = useState([]);
  const [spamDone, setSpamDone] = useState(false);
  const [labelAns, setLabelAns] = useState({});
  const [labelDone, setLabelDone] = useState(false);
  const [designAns, setDesignAns] = useState({});
  const [designDone, setDesignDone] = useState(false);
  const [showBreath, setShowBreath] = useState(false);
  const [showGratitude, setShowGratitude] = useState(false);
  const [mindfulDone, setMindfulDone] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const [showTeacher, setShowTeacher] = useState(false);
  const [viewMode, setViewMode] = useState("student");
  const [activeTab, setActiveTab] = useState("modules");

  const lesson = activeModule ? activeModule.lessons[lessonIdx] : null;

  const gainXP = (amt) => {
    setXpPop(amt);
    setTimeout(() => setXpPop(null), 1500);
    setStats(prev => {
      const next = { ...prev, xp: prev.xp + amt };
      checkAch(next);
      return next;
    });
  };

  const checkAch = (s) => {
    ACHIEVEMENTS.forEach(a => {
      if (!earned.includes(a.id) && a.check(s)) {
        setEarned(p => [...p, a.id]);
        setNewAch(a);
        setTimeout(() => setNewAch(null), 3000);
      }
    });
  };

  const resetLesson = () => {
    setQuizAns(null); setQuizDone(false);
    setSpamSel([]); setSpamDone(false);
    setLabelAns({}); setLabelDone(false);
    setDesignAns({}); setDesignDone(false);
    setShowBreath(false); setShowGratitude(false); setMindfulDone(false);
    setShowParent(false); setShowTeacher(false);
  };

  const nextLesson = () => {
    resetLesson();
    if (lessonIdx + 1 < activeModule.lessons.length) {
      setLessonIdx(lessonIdx + 1);
    } else {
      gainXP(activeModule.xp);
      setStats(prev => {
        const next = { ...prev, modules: prev.modules + 1, doneModules: [...prev.doneModules, activeModule.id] };
        checkAch(next);
        return next;
      });
      setScreen("complete");
    }
  };

  const level = Math.floor(stats.xp / 250) + 1;
  const xpInLevel = stats.xp - (level - 1) * 250;

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Fredoka+One&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Nunito',sans-serif;background:linear-gradient(135deg,#FFF9F0,#F0F9FF,#F5F0FF);}
    @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes pop{0%{transform:scale(0.5);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    @keyframes xpfly{0%{transform:translateY(0);opacity:1}100%{transform:translateY(-60px);opacity:0}}
    @keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
    .card{background:white;border-radius:22px;box-shadow:0 3px 20px rgba(0,0,0,0.07);}
    .btn{cursor:pointer;border:none;font-family:'Nunito',sans-serif;font-weight:800;border-radius:14px;transition:all 0.15s;}
    .btn:hover{transform:translateY(-2px);box-shadow:0 5px 18px rgba(0,0,0,0.12);}
    .btn:active{transform:translateY(0);}
    .mcard{cursor:pointer;border-radius:20px;transition:all 0.2s;overflow:hidden;background:white;box-shadow:0 3px 16px rgba(0,0,0,0.06);}
    .mcard:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,0.12);}
    .opt{width:100%;padding:13px 18px;text-align:left;background:#F9FAFB;border:2.5px solid #E5E7EB;border-radius:13px;cursor:pointer;font-family:'Nunito',sans-serif;font-weight:700;font-size:14px;color:#374151;transition:all 0.2s;margin-bottom:8px;display:block;}
    .opt:hover:not([disabled]){border-color:#A78BFA;background:#F5F0FF;}
    .opt.correct{border-color:#10B981!important;background:#ECFDF5!important;color:#065F46!important;}
    .opt.wrong{border-color:#EF4444!important;background:#FEF2F2!important;color:#991B1B!important;}
    .tab{padding:8px 18px;border-radius:12px;border:none;font-family:'Nunito',sans-serif;font-weight:800;font-size:13px;cursor:pointer;transition:all 0.2s;}
  `;

  // ── HOME SCREEN ──
  if (screen === "home") return (
    <div style={{ minHeight: "100vh", padding: "20px 16px", maxWidth: 640, margin: "0 auto" }}>
      <style>{css}</style>

      {xpPop && <div style={{ position: "fixed", top: 70, right: 20, zIndex: 999, animation: "xpfly 1.5s ease-out forwards", background: "#F7B731", color: "white", padding: "8px 18px", borderRadius: 999, fontWeight: 900, fontSize: 18 }}>+{xpPop} XP ⭐</div>}
      {newAch && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 999, animation: "pop 0.4s ease-out", background: "linear-gradient(135deg,#7C3AED,#EC4899)", color: "white", padding: "14px 24px", borderRadius: 20, textAlign: "center", minWidth: 240 }}>
        <div style={{ fontSize: 28, marginBottom: 4 }}>{newAch.emoji}</div>
        <div style={{ fontWeight: 900, fontSize: 15 }}>Achievement Unlocked!</div>
        <div style={{ fontWeight: 700, fontSize: 13, opacity: 0.9 }}>{newAch.title}</div>
      </div>}

      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: 22, animation: "slideUp 0.5s ease-out" }}>
        <div style={{ fontSize: 52, marginBottom: 8, animation: "float 3s ease-in-out infinite" }}>🤖</div>
        <h1 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 32, background: "linear-gradient(135deg,#FF6B9D,#F7B731,#4ECDC4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1.1, marginBottom: 6 }}>AI Learning Universe</h1>
        <p style={{ color: "#6B7280", fontWeight: 700, fontSize: 14 }}>Interactive AI education with mindfulness for ages 8–15 🌱</p>
      </div>

      {/* Stats Bar */}
      <div className="card" style={{ padding: "14px 18px", marginBottom: 18, animation: "slideUp 0.5s ease-out 0.1s both" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#7C3AED,#EC4899)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, color: "white" }}>{level}</div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 14, color: "#1F2937" }}>Level {level} Explorer</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700 }}>{stats.xp} XP earned</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {[["🏆", earned.length], ["🔥", stats.streak], ["✅", stats.modules]].map(([e, v], i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 16 }}>{e}</div>
                <div style={{ fontWeight: 900, fontSize: 14, color: "#1F2937" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: "#E5E7EB", borderRadius: 999, height: 10, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(100, (xpInLevel / 250) * 100)}%`, height: "100%", background: "linear-gradient(90deg,#7C3AED,#EC4899)", borderRadius: 999, transition: "width 0.6s ease" }} />
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700, marginTop: 4 }}>{xpInLevel}/250 XP to Level {level + 1}</div>
      </div>

      {/* View Toggle */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, background: "#F3F4F6", borderRadius: 14, padding: 5 }}>
        {[["student", "👦 Student"], ["parent", "💜 Parent"], ["teacher", "🍎 Teacher"]].map(([m, l]) => (
          <button key={m} className="tab" onClick={() => setViewMode(m)} style={{ flex: 1, background: viewMode === m ? "white" : "transparent", color: viewMode === m ? "#1F2937" : "#9CA3AF", boxShadow: viewMode === m ? "0 1px 6px rgba(0,0,0,0.08)" : "none" }}>{l}</button>
        ))}
      </div>

      {/* Parent/Teacher panel */}
      {viewMode !== "student" && (
        <div className="card" style={{ padding: "18px", marginBottom: 18, background: viewMode === "parent" ? "#FFF0F5" : "#EFF6FF", border: `2px solid ${viewMode === "parent" ? "#FF6B9D44" : "#4ECDC444"}` }}>
          <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 17, color: "#1F2937", marginBottom: 8 }}>
            {viewMode === "parent" ? "👨‍👩‍👧 Welcome, Parent!" : "🍎 Welcome, Educator!"}
          </div>
          <p style={{ fontWeight: 700, color: "#6B7280", fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
            {viewMode === "parent"
              ? "Look for 💜 Parent Tips inside each activity — they're conversation starters designed for the dinner table!"
              : "Look for 🍎 Teacher Tips inside activities — classroom projects, discussions and hands-on ideas!"}
          </p>
          <div style={{ background: "white", borderRadius: 12, padding: "12px" }}>
            <div style={{ fontWeight: 900, fontSize: 12, color: "#374151", marginBottom: 6 }}>Learners will develop:</div>
            {["AI literacy & digital confidence", "Critical thinking & ethical reasoning", "Creativity & real-world problem solving", "Mindfulness habits for focused learning"].map((item, i) => (
              <div key={i} style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", padding: "2px 0" }}>• {item}</div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["modules", "📚 Modules"], ["achievements", "🏆 Achievements"]].map(([t, l]) => (
          <button key={t} className="tab" onClick={() => setActiveTab(t)} style={{ padding: "8px 16px", background: activeTab === t ? "linear-gradient(135deg,#7C3AED,#EC4899)" : "#F3F4F6", color: activeTab === t ? "white" : "#6B7280" }}>{l}</button>
        ))}
      </div>

      {/* Modules */}
      {activeTab === "modules" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MODULES.map((mod, i) => {
            const done = stats.doneModules.includes(mod.id);
            return (
              <div key={mod.id} className="mcard" style={{ animation: `slideUp 0.5s ease-out ${0.15 + i * 0.07}s both` }}
                onClick={() => { setActiveModule(mod); setLessonIdx(0); resetLesson(); setScreen("lesson"); }}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "16px 18px" }}>
                  <div style={{ width: 54, height: 54, borderRadius: 16, background: `linear-gradient(135deg,${mod.color},${mod.color}88)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>
                    {done ? "✅" : mod.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 16, color: "#1F2937" }}>{mod.title}</div>
                        <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 700, marginTop: 2 }}>{mod.desc}</div>
                      </div>
                      <div style={{ background: `${mod.color}22`, color: mod.dark, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 900, height: "fit-content", whiteSpace: "nowrap" }}>+{mod.xp} XP</div>
                    </div>
                    <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
                      {[`👦 ${mod.age}`, `⏱ ${mod.duration}`, `🌍 ${mod.world}`, `🌿 ${mod.mindfulness}`].map((tag, j) => (
                        <span key={j} style={{ background: mod.bg, color: mod.dark, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800 }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Achievements */}
      {activeTab === "achievements" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {ACHIEVEMENTS.map(a => {
            const got = earned.includes(a.id);
            return (
              <div key={a.id} className="card" style={{ padding: "14px", textAlign: "center", opacity: got ? 1 : 0.45, filter: got ? "none" : "grayscale(1)" }}>
                <div style={{ fontSize: 28, marginBottom: 5 }}>{a.emoji}</div>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#1F2937", marginBottom: 3 }}>{a.title}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700 }}>{a.desc}</div>
                {got && <div style={{ marginTop: 6, background: "#ECFDF5", color: "#065F46", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 900 }}>Earned ✓</div>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ height: 32 }} />
    </div>
  );

  // ── LESSON SCREEN ──
  if (screen === "lesson" && activeModule && lesson) return (
    <div style={{ minHeight: "100vh", padding: "18px 16px", maxWidth: 620, margin: "0 auto" }}>
      <style>{css}</style>

      {xpPop && <div style={{ position: "fixed", top: 70, right: 20, zIndex: 999, animation: "xpfly 1.5s ease-out forwards", background: "#F7B731", color: "white", padding: "8px 18px", borderRadius: 999, fontWeight: 900, fontSize: 18 }}>+{xpPop} XP ⭐</div>}

      {/* Header */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <button className="btn" onClick={() => setScreen("home")} style={{ background: "#F3F4F6", color: "#374151", padding: "8px 14px", fontSize: 13 }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 15, color: "#1F2937" }}>{activeModule.title}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700 }}>Lesson {lessonIdx + 1} of {activeModule.lessons.length}</div>
        </div>
        <div style={{ background: `${activeModule.color}22`, color: activeModule.dark, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 900 }}>{activeModule.emoji} {Math.round((lessonIdx / activeModule.lessons.length) * 100)}%</div>
      </div>

      {/* Progress */}
      <div style={{ height: 8, background: "#E5E7EB", borderRadius: 999, marginBottom: 18, overflow: "hidden" }}>
        <div style={{ width: `${((lessonIdx + 1) / activeModule.lessons.length) * 100}%`, height: "100%", background: `linear-gradient(90deg,${activeModule.color},${activeModule.color}88)`, borderRadius: 999, transition: "width 0.5s ease" }} />
      </div>

      {/* ── MINDFUL LESSON ── */}
      {lesson.type === "mindful" && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14, background: activeModule.bg }}>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 20, color: "#1F2937", marginBottom: 12 }}>{lesson.title}</h2>
            <p style={{ fontWeight: 700, fontSize: 14, color: "#374151", lineHeight: 1.7, marginBottom: 14 }}>{lesson.content}</p>
            <div style={{ background: "white", borderRadius: 14, padding: "12px", marginBottom: 10, display: "flex", gap: 10 }}>
              <span style={{ fontSize: 16 }}>🌿</span>
              <div>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#065F46", marginBottom: 3 }}>Mindfulness tip</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>{lesson.fact}</div>
              </div>
            </div>
            <div style={{ background: `${activeModule.color}15`, borderRadius: 14, padding: "12px", display: "flex", gap: 10 }}>
              <span style={{ fontSize: 16 }}>🌍</span>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}><strong>Real world:</strong> {lesson.world}</div>
            </div>
          </div>

          {!mindfulDone && (
            <div className="card" style={{ padding: "20px", marginBottom: 14 }}>
              {!showBreath && !showGratitude ? (
                <button className="btn" onClick={() => {
                  if (lesson.activity === "breathing") setShowBreath(true);
                  else if (lesson.activity === "gratitude") setShowGratitude(true);
                  else setMindfulDone(true);
                }}
                  style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white" }}>
                  {lesson.activity === "breathing" ? "🌬️ Start Breathing Exercise" : lesson.activity === "gratitude" ? "💛 Start Gratitude Practice" : "🧘 I've Done My Mindful Moment"}
                </button>
              ) : showBreath ? (
                <BreathingEx color={activeModule.color} onDone={() => {
                  setShowBreath(false); setMindfulDone(true);
                  setStats(p => { const n = { ...p, mindful: p.mindful + 1, lessons: p.lessons + 1 }; checkAch(n); return n; });
                  gainXP(30);
                }} />
              ) : showGratitude ? (
                <GratitudeEx color={activeModule.color} onDone={() => {
                  setShowGratitude(false); setMindfulDone(true);
                  setStats(p => { const n = { ...p, mindful: p.mindful + 1, lessons: p.lessons + 1 }; checkAch(n); return n; });
                  gainXP(30);
                }} />
              ) : null}
            </div>
          )}

          {mindfulDone && (
            <button className="btn" onClick={nextLesson} style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", animation: "pop 0.3s ease-out" }}>
              My Mind is Ready! →
            </button>
          )}
          {!mindfulDone && !showBreath && !showGratitude && lesson.activity !== "breathing" && lesson.activity !== "gratitude" && (
            <button className="btn" onClick={() => { setMindfulDone(true); setStats(p => { const n = { ...p, mindful: p.mindful + 1, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(30); }}
              style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", marginTop: 8 }}>
              Done! Continue →
            </button>
          )}
        </div>
      )}

      {/* ── LEARN LESSON ── */}
      {lesson.type === "learn" && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14, background: activeModule.bg }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12, animation: "bounce 2s ease-in-out infinite" }}>{activeModule.emoji}</div>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 20, color: "#1F2937", textAlign: "center", marginBottom: 14 }}>{lesson.title}</h2>
            <p style={{ fontWeight: 700, fontSize: 15, color: "#374151", lineHeight: 1.7, marginBottom: 14 }}>{lesson.content}</p>
            <div style={{ background: "#FFF9C4", borderRadius: 14, padding: "12px", marginBottom: 10, display: "flex", gap: 10 }}>
              <span style={{ fontSize: 16 }}>💡</span>
              <div>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#92400E", marginBottom: 3 }}>Fun fact!</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#78350F" }}>{lesson.fact}</div>
              </div>
            </div>
            <div style={{ background: `${activeModule.color}15`, borderRadius: 14, padding: "12px", display: "flex", gap: 10 }}>
              <span style={{ fontSize: 16 }}>🌍</span>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}><strong>Real world:</strong> {lesson.world}</div>
            </div>
          </div>
          <button className="btn" onClick={() => { setStats(p => { const n = { ...p, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(20); nextLesson(); }}
            style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white" }}>
            Got it! Next →
          </button>
        </div>
      )}

      {/* ── QUIZ LESSON ── */}
      {lesson.type === "quiz" && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14 }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 10 }}>🤔</div>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color: "#1F2937", textAlign: "center", marginBottom: 14 }}>{lesson.question}</h2>
            {lesson.options.map((opt, i) => {
              let cls = "opt";
              if (quizDone) cls += i === lesson.correct ? " correct" : i === quizAns ? " wrong" : "";
              return (
                <button key={i} className={cls} disabled={quizDone} onClick={() => {
                  setQuizAns(i); setQuizDone(true);
                  const ok = i === lesson.correct;
                  setStats(p => {
                    const streak = ok ? p.streak + 1 : 0;
                    const n = { ...p, correct: ok ? p.correct + 1 : p.correct, streak, lessons: p.lessons + 1 };
                    checkAch(n); return n;
                  });
                  if (ok) gainXP(50);
                }}>
                  <span style={{ marginRight: 8 }}>{quizDone ? (i === lesson.correct ? "✅" : i === quizAns ? "❌" : `${i + 1}.`) : `${i + 1}.`}</span>{opt}
                </button>
              );
            })}
            {quizDone && (
              <div style={{ animation: "pop 0.3s ease-out", background: quizAns === lesson.correct ? "#ECFDF5" : "#FFF7F0", borderRadius: 14, padding: "12px", marginTop: 4, border: `2px solid ${quizAns === lesson.correct ? "#10B981" : "#F59E0B"}` }}>
                <div style={{ fontWeight: 900, fontSize: 14, color: quizAns === lesson.correct ? "#065F46" : "#92400E", marginBottom: 3 }}>
                  {quizAns === lesson.correct ? "🎉 Brilliant!" : "💪 Good try! Here's why:"}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>{lesson.explain}</div>
              </div>
            )}
          </div>
          {quizDone && (
            <button className="btn" onClick={nextLesson} style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", animation: "pop 0.3s ease-out" }}>
              Next →
            </button>
          )}
        </div>
      )}

      {/* ── SCENARIO / SPAM ── */}
      {lesson.type === "scenario" && !lesson.isDesign && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14 }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 10 }}>🕵️</div>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color: "#1F2937", textAlign: "center", marginBottom: 6 }}>{lesson.title}</h2>
            <p style={{ textAlign: "center", fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 16 }}>{lesson.desc}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {lesson.items.map((item, i) => {
                const sel = spamSel.includes(i);
                const isSpam = lesson.spam.includes(i);
                return (
                  <div key={i} onClick={() => !spamDone && setSpamSel(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i])}
                    style={{ padding: "12px 16px", borderRadius: 12, border: `2.5px solid ${spamDone ? (isSpam ? "#EF4444" : "#10B981") : sel ? "#F59E0B" : "#E5E7EB"}`, background: spamDone ? (isSpam ? "#FEF2F2" : "#ECFDF5") : sel ? "#FFF9F0" : "#F9FAFB", cursor: spamDone ? "default" : "pointer", fontWeight: 700, fontSize: 14, color: "#374151", display: "flex", justifyContent: "space-between", transition: "all 0.2s" }}>
                    <span>📧 {item}</span>
                    {spamDone && <span style={{ fontWeight: 900, fontSize: 12 }}>{isSpam ? "🚫 SPAM" : "✅ Safe"}</span>}
                    {!spamDone && sel && <span>🚫</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ background: "#EFF6FF", borderRadius: 12, padding: "10px 14px", marginBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1E40AF" }}>💡 {lesson.hint}</span>
            </div>
            <div style={{ background: `${activeModule.color}15`, borderRadius: 12, padding: "10px 14px" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>🌍 {lesson.world}</span>
            </div>
          </div>
          {!spamDone ? (
            <button className="btn" onClick={() => { setSpamDone(true); setStats(p => { const n = { ...p, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(60); }}
              style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white" }}>
              Check My Answers! 🔍
            </button>
          ) : (
            <button className="btn" onClick={nextLesson} style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", animation: "pop 0.3s ease-out" }}>
              Next →
            </button>
          )}
        </div>
      )}

      {/* ── DESIGN SCENARIO ── */}
      {lesson.type === "scenario" && lesson.isDesign && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14 }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 10 }}>🎯</div>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color: "#1F2937", textAlign: "center", marginBottom: 6 }}>{lesson.title}</h2>
            <p style={{ textAlign: "center", fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 16 }}>{lesson.desc}</p>
            {lesson.items.map((item, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#374151", marginBottom: 6 }}>Problem: {item}</div>
                <select disabled={designDone} value={designAns[i] || ""} onChange={e => setDesignAns(p => ({ ...p, [i]: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 12, border: `2px solid ${designDone ? (designAns[i] === lesson.solutions[i] ? "#10B981" : "#EF4444") : "#E5E7EB"}`, background: designDone ? (designAns[i] === lesson.solutions[i] ? "#ECFDF5" : "#FEF2F2") : "white", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 13, cursor: designDone ? "default" : "pointer" }}>
                  <option value="">Choose the best AI solution...</option>
                  {lesson.solutions.map((s, j) => <option key={j} value={s}>{s}</option>)}
                </select>
                {designDone && <div style={{ fontSize: 12, fontWeight: 700, color: designAns[i] === lesson.solutions[i] ? "#065F46" : "#991B1B", marginTop: 4 }}>
                  {designAns[i] === lesson.solutions[i] ? "✅ Perfect match!" : `✗ Best answer: ${lesson.solutions[i]}`}
                </div>}
              </div>
            ))}
            <div style={{ background: "#EFF6FF", borderRadius: 12, padding: "10px 14px", marginTop: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1E40AF" }}>💡 {lesson.hint}</span>
            </div>
          </div>
          {!designDone ? (
            <button className="btn" disabled={Object.keys(designAns).length < lesson.items.length}
              onClick={() => { setDesignDone(true); setStats(p => { const n = { ...p, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(70); }}
              style={{ width: "100%", padding: "14px", fontSize: 15, background: Object.keys(designAns).length < lesson.items.length ? "#E5E7EB" : `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: Object.keys(designAns).length < lesson.items.length ? "#9CA3AF" : "white" }}>
              Check My Design! 🔍
            </button>
          ) : (
            <button className="btn" onClick={nextLesson} style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", animation: "pop 0.3s ease-out" }}>
              Next →
            </button>
          )}
        </div>
      )}

      {/* ── LABEL LESSON ── */}
      {lesson.type === "label" && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14 }}>
            <div style={{ fontSize: 34, textAlign: "center", marginBottom: 10 }}>🏷️</div>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 18, color: "#1F2937", textAlign: "center", marginBottom: 6 }}>{lesson.title}</h2>
            <p style={{ textAlign: "center", fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 16 }}>{lesson.desc}</p>
            {lesson.items.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 12, marginBottom: 8, border: `2.5px solid ${labelDone ? (labelAns[i] === lesson.labels[i] ? "#10B981" : "#EF4444") : "#E5E7EB"}`, background: labelDone ? (labelAns[i] === lesson.labels[i] ? "#ECFDF5" : "#FEF2F2") : "#F9FAFB" }}>
                <span style={{ fontSize: 24 }}>{lesson.emojis[i]}</span>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: "#374151" }}>{item}</span>
                {!labelDone ? (
                  <select value={labelAns[i] || ""} onChange={e => setLabelAns(p => ({ ...p, [i]: e.target.value }))}
                    style={{ padding: "6px 10px", borderRadius: 8, border: "2px solid #E5E7EB", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    <option value="">Label?</option>
                    {lesson.labels.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                ) : (
                  <span style={{ fontWeight: 900, fontSize: 12, color: labelAns[i] === lesson.labels[i] ? "#065F46" : "#991B1B" }}>
                    {labelAns[i] === lesson.labels[i] ? `✅ ${lesson.labels[i]}` : `❌ → ${lesson.labels[i]}`}
                  </span>
                )}
              </div>
            ))}
            <div style={{ background: "#EFF6FF", borderRadius: 12, padding: "10px 14px", marginTop: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1E40AF" }}>💡 {lesson.hint}</span>
            </div>
            <div style={{ background: `${activeModule.color}15`, borderRadius: 12, padding: "10px 14px", marginTop: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>🌍 {lesson.world}</span>
            </div>
          </div>
          {!labelDone ? (
            <button className="btn" disabled={Object.keys(labelAns).length < lesson.items.length}
              onClick={() => { setLabelDone(true); setStats(p => { const n = { ...p, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(60); }}
              style={{ width: "100%", padding: "14px", fontSize: 15, background: Object.keys(labelAns).length < lesson.items.length ? "#E5E7EB" : `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: Object.keys(labelAns).length < lesson.items.length ? "#9CA3AF" : "white" }}>
              Check Labels! 🏷️
            </button>
          ) : (
            <button className="btn" onClick={nextLesson} style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white", animation: "pop 0.3s ease-out" }}>
              Next →
            </button>
          )}
        </div>
      )}

      {/* ── ACTIVITY LESSON ── */}
      {lesson.type === "activity" && (
        <div style={{ animation: "slideUp 0.4s ease-out" }}>
          <div className="card" style={{ padding: "22px", marginBottom: 14, background: activeModule.bg }}>
            <h2 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 20, color: "#1F2937", marginBottom: 12 }}>{lesson.title}</h2>
            <div style={{ background: "white", borderRadius: 14, padding: "14px", marginBottom: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 13, color: "#1F2937", marginBottom: 8 }}>📋 What to do:</div>
              <p style={{ fontWeight: 700, fontSize: 13, color: "#374151", lineHeight: 1.7 }}>{lesson.instructions}</p>
            </div>
            <div style={{ background: `${activeModule.color}15`, borderRadius: 14, padding: "12px", marginBottom: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 12, color: activeModule.dark, marginBottom: 6 }}>🌍 Real world connection:</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>{lesson.world}</div>
            </div>
            <div style={{ background: "#F5F0FF", borderRadius: 14, padding: "12px", marginBottom: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 12, color: "#5B21B6", marginBottom: 6 }}>🧠 Think about it:</div>
              {lesson.prompts.map((p, i) => <div key={i} style={{ fontWeight: 700, fontSize: 13, color: "#374151", padding: "3px 0" }}>💭 {p}</div>)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {lesson.parentTip && <>
                <button onClick={() => setShowParent(!showParent)} style={{ background: "#FFF0F5", border: "none", borderRadius: 12, padding: "9px 14px", cursor: "pointer", fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 12, color: "#BE185D", textAlign: "left" }}>
                  💜 Parent Tip {showParent ? "▲" : "▼"}
                </button>
                {showParent && <div style={{ background: "#FFF0F5", borderRadius: "0 0 12px 12px", padding: "10px 14px", fontWeight: 700, fontSize: 12, color: "#BE185D", marginTop: -8 }}>{lesson.parentTip}</div>}
              </>}
              {lesson.teacherTip && <>
                <button onClick={() => setShowTeacher(!showTeacher)} style={{ background: "#EFF6FF", border: "none", borderRadius: 12, padding: "9px 14px", cursor: "pointer", fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 12, color: "#1E40AF", textAlign: "left" }}>
                  🍎 Teacher Tip {showTeacher ? "▲" : "▼"}
                </button>
                {showTeacher && <div style={{ background: "#EFF6FF", borderRadius: "0 0 12px 12px", padding: "10px 14px", fontWeight: 700, fontSize: 12, color: "#1E40AF", marginTop: -8 }}>{lesson.teacherTip}</div>}
              </>}
            </div>
          </div>
          <button className="btn" onClick={() => { setStats(p => { const n = { ...p, lessons: p.lessons + 1 }; checkAch(n); return n; }); gainXP(40); nextLesson(); }}
            style={{ width: "100%", padding: "14px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.color}88)`, color: "white" }}>
            Activity Done! ✨ Next →
          </button>
        </div>
      )}

      <div style={{ height: 24 }} />
    </div>
  );

  // ── COMPLETE SCREEN ──
  if (screen === "complete" && activeModule) return (
    <div style={{ minHeight: "100vh", padding: "48px 20px", maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
      <style>{css}</style>
      <div style={{ fontSize: 68, marginBottom: 14, animation: "float 2s ease-in-out infinite" }}>🎉</div>
      <h1 style={{ fontFamily: "'Fredoka One',cursive", fontSize: 28, background: `linear-gradient(135deg,${activeModule.color},${activeModule.dark})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 8 }}>Module Complete!</h1>
      <p style={{ fontWeight: 700, color: "#6B7280", fontSize: 14, marginBottom: 24 }}>You just mastered {activeModule.title}! 🎓</p>
      <div className="card" style={{ padding: "22px", marginBottom: 20, background: activeModule.bg }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>{activeModule.emoji}</div>
        <div style={{ fontFamily: "'Fredoka One',cursive", fontSize: 19, color: "#1F2937", marginBottom: 6 }}>{activeModule.title}</div>
        <div style={{ fontWeight: 700, color: "#6B7280", fontSize: 13, marginBottom: 16 }}>Real world: {activeModule.world}</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 24 }}>
          {[["⭐", stats.xp, "Total XP"], ["✅", stats.modules, "Modules"], ["🧘", stats.mindful, "Mindful"]].map(([e, v, l], i) => (
            <div key={i}><div style={{ fontSize: 20 }}>{e}</div><div style={{ fontWeight: 900, fontSize: 20, color: "#1F2937" }}>{v}</div><div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700 }}>{l}</div></div>
          ))}
        </div>
      </div>
      <div className="card" style={{ padding: "16px", marginBottom: 20, textAlign: "left" }}>
        <div style={{ fontWeight: 900, fontSize: 13, color: "#1F2937", marginBottom: 8 }}>💭 Reflect:</div>
        {["What surprised you most?", "How does this connect to YOUR daily life?", "What question do you still have?"].map((q, i) => (
          <div key={i} style={{ fontWeight: 700, fontSize: 12, color: "#6B7280", padding: "4px 0" }}>✦ {q}</div>
        ))}
      </div>
      <button className="btn" onClick={() => setScreen("home")} style={{ width: "100%", padding: "15px", fontSize: 15, background: `linear-gradient(135deg,${activeModule.color},${activeModule.dark})`, color: "white", marginBottom: 10 }}>
        🚀 Keep Exploring!
      </button>
      <p style={{ color: "#9CA3AF", fontWeight: 700, fontSize: 12 }}>Your curiosity is your superpower 🌱</p>
    </div>
  );

  return null;
}